/** Save-duration estimate for the Data Builder. Throughput is config-driven
 *  (data-builder.json → `saveEstimate`) since it's a backend property. */

export interface SaveEstimateConfig {
  rowsPerMinute?: number;
  lowFactor?: number;
  highFactor?: number;
  minMinutes?: number;
  /** Below this row count the estimate isn't shown. */
  showFromRows?: number;
}

const DEFAULTS: Required<SaveEstimateConfig> = {
  rowsPerMinute: 14000,
  lowFactor: 1,
  highFactor: 1.4,
  minMinutes: 1,
  showFromRows: 5000,
};

export interface SaveEstimate {
  rows: number;
  lowMinutes: number;
  highMinutes: number;
  expectedSeconds: number;
  worthShowing: boolean;
}

function resolve(config: SaveEstimateConfig | undefined): Required<SaveEstimateConfig> {
  const merged = { ...DEFAULTS, ...(config ?? {}) };
  // Guard: a zero/negative rate divides to Infinity and renders "∞ min".
  if (!Number.isFinite(merged.rowsPerMinute) || merged.rowsPerMinute <= 0) {
    merged.rowsPerMinute = DEFAULTS.rowsPerMinute;
  }
  return merged;
}

export function estimateSave(rows: number, config?: SaveEstimateConfig): SaveEstimate {
  const cfg = resolve(config);
  const safeRows = Math.max(0, Math.floor(Number(rows) || 0));
  const baseMinutes = safeRows / cfg.rowsPerMinute;
  const lowMinutes = Math.max(cfg.minMinutes, Math.round(baseMinutes * cfg.lowFactor));
  const highMinutes = Math.max(lowMinutes, Math.round(baseMinutes * cfg.highFactor));
  return {
    rows: safeRows,
    lowMinutes,
    highMinutes,
    expectedSeconds: ((lowMinutes + highMinutes) / 2) * 60,
    worthShowing: safeRows >= cfg.showFromRows,
  };
}

export function formatEstimateRange(estimate: SaveEstimate): string {
  return estimate.lowMinutes === estimate.highMinutes
    ? `${estimate.lowMinutes} min`
    : `${estimate.lowMinutes}-${estimate.highMinutes} min`;
}

/** Indian digit grouping (1,00,000). */
export function formatRowCount(rows: number): string {
  return rows.toLocaleString('en-IN');
}

export function formatElapsed(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}
